import { useCallback, useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { AlertCircle, CheckCircle2, Trash2 } from 'lucide-react'
import { validateIban, isSepaIban } from '@/lib/iban-validator'
import { validateSepaQrData, type SepaQrData } from '@/lib/sepa-qr'
import { loadStoredData, saveStoredData, clearStoredData } from '@/lib/storage'
import { getSortedPurposeCodes, getPurposeCode } from '@/lib/purpose-codes'
import { generateRfReference } from '@/lib/creditor-reference'

interface SepaFormProps {
  initialData?: Partial<SepaQrData>
  onDataChange: (data: SepaQrData, isValid: boolean) => void
}

/**
 * SEPA RTP form component with validation and localStorage persistence
 */
export function SepaForm({ initialData, onDataChange }: SepaFormProps) {
  // Form state
  const [name, setName] = useState('')
  const [iban, setIban] = useState('')
  const [bic, setBic] = useState('')
  const [amount, setAmount] = useState('')
  const [purpose, setPurpose] = useState('')
  const [referenceInput, setReferenceInput] = useState('')
  const [useRfFormat, setUseRfFormat] = useState(true)
  const [text, setText] = useState('')
  const [information, setInformation] = useState('')
  const [instant, setInstant] = useState(false)
  const [showBic, setShowBic] = useState(false)

  // Validation state
  const [ibanError, setIbanError] = useState<string | null>(null)
  const [ibanValid, setIbanValid] = useState(false)
  const [isSepa, setIsSepa] = useState(true)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [formWarnings, setFormWarnings] = useState<string[]>([])
  const [payloadSize, setPayloadSize] = useState(0)

  // Load stored data on mount
  useEffect(() => {
    const stored = loadStoredData()
    if (!initialData?.name && stored.name) setName(stored.name)
    if (!initialData?.iban && stored.iban) setIban(stored.iban)
    if (!initialData?.bic && stored.bic) {
      setBic(stored.bic)
      setShowBic(true)
    }
    if (!initialData?.amount && stored.amount) setAmount(stored.amount)
  }, [])

  // Apply initial data from URL params
  useEffect(() => {
    if (initialData) {
      if (initialData.name) setName(initialData.name)
      if (initialData.iban) setIban(initialData.iban)
      if (initialData.bic) {
        setBic(initialData.bic)
        setShowBic(true)
      }
      if (initialData.amount) setAmount(initialData.amount.toString())
      if (initialData.purpose) setPurpose(initialData.purpose)
      if (initialData.reference) {
        // Check if it's already an RF reference
        if (initialData.reference.toUpperCase().startsWith('RF')) {
          const cleaned = initialData.reference.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
          setReferenceInput(cleaned.slice(4)) // Extract the reference part after RF+checkdigits
          setUseRfFormat(true)
        } else {
          setReferenceInput(initialData.reference)
          setUseRfFormat(false)
        }
      }
      if (initialData.text) setText(initialData.text)
      if (initialData.information) setInformation(initialData.information)
      if (initialData.instant) setInstant(initialData.instant)
    }
  }, [initialData])

  // Validate IBAN on change
  useEffect(() => {
    if (!iban) {
      setIbanError(null)
      setIbanValid(false)
      setIsSepa(true)
      return
    }

    const result = validateIban(iban)
    if (result.valid) {
      setIbanError(null)
      setIbanValid(true)
      setIsSepa(isSepaIban(iban))
    } else {
      setIbanError(result.errors[0] || 'Invalid IBAN')
      setIbanValid(false)
      setIsSepa(true)
    }
  }, [iban])

  // Save persisted fields to localStorage
  useEffect(() => {
    saveStoredData({ name, iban, bic, amount })
  }, [name, iban, bic, amount])

  // Compute the final reference value
  const computedReference = useCallback(() => {
    if (!referenceInput.trim()) return undefined
    if (useRfFormat) {
      return generateRfReference(referenceInput.trim())
    }
    return referenceInput.trim()
  }, [referenceInput, useRfFormat])

  // Build and validate form data
  const buildFormData = useCallback((): SepaQrData => {
    return {
      name: name.trim(),
      iban: iban.replace(/\s/g, '').toUpperCase(),
      bic: bic.trim() || undefined,
      amount: amount ? parseFloat(amount) : undefined,
      purpose: purpose.trim() || undefined,
      reference: computedReference() || undefined,
      text: text.trim() || undefined,
      information: information.trim() || undefined,
      charset: 1, // Always use UTF-8
      instant
    }
  }, [name, iban, bic, amount, purpose, computedReference, text, information, instant])

  // Validate and notify parent on any change
  useEffect(() => {
    const data = buildFormData()
    const validation = validateSepaQrData(data)
    setFormErrors(validation.errors)
    setFormWarnings(validation.warnings)
    setPayloadSize(validation.payloadSize)
    onDataChange(data, validation.valid)
  }, [buildFormData, onDataChange])

  // Handle clear stored data
  const handleClearStored = () => {
    clearStoredData()
    setName('')
    setIban('')
    setBic('')
    setAmount('')
    setShowBic(false)
  }

  // Handle IBAN formatting on blur
  const handleIbanBlur = () => {
    if (iban) {
      const cleaned = iban.replace(/\s/g, '').toUpperCase()
      const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim()
      setIban(formatted)
    }
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg sm:text-xl">Payment Details</CardTitle>
        <CardDescription>
          Enter the beneficiary information for your SEPA Request-to-Pay
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 sm:space-y-6">
        {/* Beneficiary Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Beneficiary
          </h3>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company or person name"
              maxLength={70}
            />
            <p className="text-xs text-muted-foreground">{name.length}/70 characters</p>
          </div>

          {/* IBAN */}
          <div className="space-y-2">
            <Label htmlFor="iban">
              IBAN <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="iban"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                onBlur={handleIbanBlur}
                placeholder="FR76 3000 6000 0112 3456 7890 189"
                className={ibanError ? 'border-destructive' : ibanValid ? 'border-green-500' : ''}
              />
              {ibanValid && (
                <CheckCircle2 className="absolute right-3 top-2.5 h-4 w-4 text-green-500" />
              )}
            </div>
            {ibanError && (
              <p className="text-xs text-destructive">{ibanError}</p>
            )}
            {ibanValid && !isSepa && (
              <p className="text-xs text-amber-600">
                Warning: This IBAN is not from a SEPA country
              </p>
            )}
          </div>

          {/* BIC Toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="show-bic"
              checked={showBic}
              onCheckedChange={setShowBic}
            />
            <Label htmlFor="show-bic" className="text-sm">
              Include BIC (optional for EEA countries)
            </Label>
          </div>

          {/* BIC */}
          {showBic && (
            <div className="space-y-2">
              <Label htmlFor="bic">BIC / SWIFT Code</Label>
              <Input
                id="bic"
                value={bic}
                onChange={(e) => setBic(e.target.value.toUpperCase())}
                placeholder="BNPAFRPP"
                maxLength={11}
              />
            </div>
          )}
        </div>

        <Separator />

        {/* Payment Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Payment
          </h3>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount (EUR) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="amount"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                // Allow digits, dots, and commas
                const value = e.target.value.replace(/[^0-9.,]/g, '')
                setAmount(value)
              }}
              onBlur={() => {
                if (amount) {
                  // Convert comma to dot for decimal separator
                  const normalized = amount.replace(',', '.')
                  const num = parseFloat(normalized)
                  if (!isNaN(num) && num > 0) {
                    setAmount(num.toFixed(2))
                  }
                }
              }}
              placeholder="0.00"
            />
          </div>

          {/* Purpose */}
          <div className="space-y-2">
            <Label htmlFor="purpose">Purpose Code (ISO 20022)</Label>
            <Select
              value={purpose || '_none'}
              onValueChange={(v) => setPurpose(v === '_none' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select purpose...">
                  {purpose ? (
                    <span className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-1 rounded">{purpose}</code>
                      <span className="text-muted-foreground">
                        {getPurposeCode(purpose)?.name || 'Unknown'}
                      </span>
                    </span>
                  ) : (
                    'Select purpose (optional)'
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="_none">
                  <span className="text-muted-foreground">No purpose code</span>
                </SelectItem>
                {getSortedPurposeCodes().map((p) => (
                  <SelectItem key={p.code} value={p.code}>
                    <span className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                        {p.code}
                      </code>
                      <span>{p.name}</span>
                      <span className="text-xs text-muted-foreground ml-1">
                        – {p.description}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {purpose && (
              <p className="text-xs text-muted-foreground">
                <code className="font-mono">{purpose}</code>: {getPurposeCode(purpose)?.description || 'Custom code'}
              </p>
            )}
          </div>
        </div>

        <Separator />

        {/* Remittance Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Remittance Information
          </h3>
          <p className="text-xs text-muted-foreground">
            Use either a structured reference OR unstructured text (not both)
          </p>

          {/* Reference Type Toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="use-rf"
              checked={useRfFormat}
              onCheckedChange={(checked) => {
                setUseRfFormat(checked)
                if (checked && referenceInput) setText('')
              }}
              disabled={!!text}
            />
            <Label htmlFor="use-rf" className="text-sm">
              Use RF Creditor Reference (ISO 11649)
            </Label>
          </div>

          {/* Reference Input */}
          <div className="space-y-2">
            <Label htmlFor="reference">
              {useRfFormat ? 'Your Reference Number' : 'Reference'}
            </Label>
            {useRfFormat ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-muted rounded-md border">
                    <span className="px-3 py-2 text-sm font-mono text-muted-foreground border-r">
                      RF
                    </span>
                    <span className="px-2 py-2 text-sm font-mono font-bold min-w-[2ch] text-center">
                      {referenceInput ? generateRfReference(referenceInput).slice(2, 4) : '??'}
                    </span>
                  </div>
                  <Input
                    id="reference"
                    value={referenceInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
                      setReferenceInput(value)
                      if (value) setText('')
                    }}
                    placeholder="Your invoice/order number"
                    maxLength={21}
                    disabled={!!text}
                    className="font-mono"
                  />
                </div>
                {referenceInput && (
                  <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-md">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <span className="text-muted-foreground block">Generated reference:</span>
                      <code className="font-mono font-bold break-all">
                        {generateRfReference(referenceInput)}
                      </code>
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Enter your reference (e.g., invoice number). The RF prefix and check digits are calculated automatically.
                </p>
              </div>
            ) : (
              <Input
                id="reference"
                value={referenceInput}
                onChange={(e) => {
                  setReferenceInput(e.target.value)
                  if (e.target.value) setText('')
                }}
                placeholder="Custom reference format"
                maxLength={35}
                disabled={!!text}
              />
            )}
          </div>

          {/* Text */}
          <div className="space-y-2">
            <Label htmlFor="text">Unstructured Text</Label>
            <Input
              id="text"
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                if (e.target.value) setReferenceInput('')
              }}
              placeholder="Invoice #12345"
              maxLength={140}
              disabled={!!referenceInput}
            />
            <p className="text-xs text-muted-foreground">{text.length}/140 characters</p>
          </div>

          {/* Information */}
          <div className="space-y-2">
            <Label htmlFor="information">Additional Information</Label>
            <Input
              id="information"
              value={information}
              onChange={(e) => setInformation(e.target.value)}
              placeholder="Thank you for your payment"
              maxLength={70}
            />
          </div>
        </div>

        <Separator />

        {/* Options Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Options
          </h3>

          {/* Instant SCT */}
          <div className="flex items-center space-x-2">
            <Switch
              id="instant"
              checked={instant}
              onCheckedChange={setInstant}
            />
            <Label htmlFor="instant" className="text-sm">
              Instant SEPA Credit Transfer (SCT Inst)
            </Label>
          </div>

          {/* Payload Size */}
          <div className="text-xs text-muted-foreground">
            Payload size: {payloadSize} / 331 bytes
            <div className="w-full bg-secondary rounded-full h-1.5 mt-1">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  payloadSize > 331 ? 'bg-destructive' : payloadSize > 297 ? 'bg-amber-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min((payloadSize / 331) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Validation Messages */}
        {formErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside">
                {formErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {formWarnings.length > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside">
                {formWarnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Clear Stored Data */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearStored}
          className="text-muted-foreground"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Clear saved data
        </Button>
      </CardContent>
    </Card>
  )
}

